import React from 'react';
import CloseIcon from '@mui/icons-material/Close';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import { AI_COLORS, FILTERS_DRAWER_WIDTH, POINTER_INPUT_SX } from '../aiAnalyticsConstants';

import { getValuesForField, replaceValuesForField } from './ChartFilters.service';

export default function ChartFilters({ open, onClose, availableFields, filters, onChange }) {
  const activeCount = filters.length;

  function handleClearAll() {
    onChange([]);
  }

  function handleFieldChange(field, newValues, allValues) {
    onChange(replaceValuesForField(filters, field, newValues, allValues));
  }

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box sx={{ width: FILTERS_DRAWER_WIDTH, display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.5 }}>
          <Typography variant="h6">Filters{activeCount > 0 ? ` (${activeCount})` : ''}</Typography>
          <IconButton onClick={onClose} size="small" aria-label="Close filters">
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider />

        <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
          {availableFields.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No fields available. Charts on this page have no data yet.
            </Typography>
          ) : (
            availableFields.map(entry => {
              const currentValues = getValuesForField(filters, entry.field, entry.values);
              const allChecked = currentValues.length === entry.values.length;

              return (
                <Box key={entry.field} sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {entry.field}
                    </Typography>
                    <Button
                      size="small"
                      onClick={() => handleFieldChange(entry.field, allChecked ? [] : entry.values, entry.values)}
                      sx={{ textTransform: 'none', minWidth: 0, p: 0.25, color: AI_COLORS.brand }}
                    >
                      {allChecked ? 'Clear' : 'Select all'}
                    </Button>
                  </Box>
                  <Autocomplete
                    multiple
                    size="small"
                    limitTags={2}
                    disableCloseOnSelect
                    options={entry.values}
                    value={currentValues}
                    onChange={(event, newValues) => handleFieldChange(entry.field, newValues, entry.values)}
                    getOptionLabel={option => (option === null || option === undefined ? '' : String(option))}
                    isOptionEqualToValue={(option, value) => option === value}
                    renderOption={(props, option, { selected }) => (
                      <li {...props}>
                        <Checkbox size="small" checked={selected} sx={{ mr: 1, p: 0.5 }} />
                        {String(option)}
                      </li>
                    )}
                    renderInput={params => (
                      <TextField
                        {...params}
                        placeholder={currentValues.length ? '' : 'No values selected'}
                        inputProps={{ ...params.inputProps, readOnly: true }}
                        sx={POINTER_INPUT_SX}
                      />
                    )}
                  />
                </Box>
              );
            })
          )}
        </Box>

        <Divider />
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between' }}>
          <Button onClick={handleClearAll} disabled={activeCount === 0}>
            Reset all
          </Button>
          <Button
            variant="contained"
            onClick={onClose}
            sx={{ backgroundColor: AI_COLORS.brand, '&:hover': { backgroundColor: AI_COLORS.brandDark } }}
          >
            Done
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
}
